import { PosterStage } from "./stage/PosterStage";
import styles from "./PosterEditor.module.css";
import { PosterProjectProvider } from "./model/poster-project-provider";
import { EditorUIProvider, useEditorUI } from "./use-editor-ui";
import { PosterEditorToolbar } from "./sidebar/poster-editor-toolbar";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { PosterProject } from "./model/poster-project";
import { ProjectTemplate } from "./model/project-template";
import { useState, useEffect } from "react";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { usePaths } from "BEDROCK/paths";
import { usePosterProject } from "./model/poster-project-provider";
import { ThreeDot } from "react-loading-indicators";
import { useLocation } from "react-router-dom";
import { useTouchMode } from "BEDROCK/mobile";
import { useHasPointer } from "./hooks/useHasPointer";
import { NoPageContent } from "BEDROCK/shared-components/no-content";
import { AuthGateModal } from "BEDROCK/shared-components/auth-gate-modal";
import { TooltipProvider } from "./tooltips";
import { useNavigationBarContext } from "navigation-bar/navigation-bar-provider";
import { UIIcon } from "./common-components/ui-icon-drawing/ui-icon";
import { useAnalyticsTracker } from "analytics-tracker";
import { useToast } from "BEDROCK/shared-components/toast";
import { hasEmptyViewports } from "./utils/gallery-utils";
import { MobileEmailCapture } from "BEDROCK/shared-components/mobile-email-capture";
import { useStravaLabelPopulation } from "PosterEditor/hooks/use-strava-label-population";
import { useCheckoutPricing } from "hooks/use-checkout-pricing";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

export type EditorContextType = "editing" | "rendering";

interface PosterEditorProps {
  //Will likely be passed in when used as a direct component rather than a linked page.
  //So this will probably be when it's nested in process-order.
  projectID?: string;
  contextType?: EditorContextType; //Remove
}

export const PosterEditor = (props: PosterEditorProps) => {
  const { loadProject, isError } = useProjectStore();
  const isTouchMode = useTouchMode();
  const hasPointer = useHasPointer();

  // Mobile is now supported - use isMobile for layout switching, not blocking
  const hasCompatibleDevice = true; // Previously: !isMobile && hasPointer

  const {
    loadTemplate,
    isLoading: isLoadingTemplate,
    isFetching: isFetchingTemplate,
    isError: isErrorTemplate,
  } = useTemplateStore();

  const [initialProject, setInitialProject] = useState<
    PosterProject | undefined
  >();
  const [template, setTemplate] = useState<ProjectTemplate | undefined>();
  const [contextType, setContextType] = useState<EditorContextType>("editing");

  const location = useLocation();

  //Get the id from the URL last part and load project or template

  useEffect(() => {
    const fetchData = async () => {
      setInitialProject(undefined);
      setTemplate(undefined);

      if (props.projectID) {
        const project = await loadProject(props.projectID);
        setInitialProject(project);
      } else {
        const path = window.location.pathname;
        const parts = path.split("/");
        const id = parts[parts.length - 1];
        const type = parts[parts.length - 2];
        const process = new URLSearchParams(window.location.search).get(
          "process",
        );

        if (!id) return;

        if (type === "project") {
          const project = await loadProject(id);
          setInitialProject(project);
          if (process === "true") {
            const url = new URL(window.location.href);
            url.searchParams.delete("process");
            window.history.replaceState(
              {},
              document.title,
              url.pathname + url.search,
            );
            setContextType("rendering");
          }
        } else if (type === "template") {
          const template = await loadTemplate(id);
          setTemplate(template);
        }
      }
    };

    fetchData();
  }, [props.projectID, location.key]);

  useEffect(() => {}, [initialProject]);
  if (!hasCompatibleDevice) {
    return <IncompatibleDeviceWarning />;
  }

  if (isLoadingTemplate || isFetchingTemplate) {
    return <Loading />;
  }

  const object = initialProject || template;

  if (!object) {
    return (
      <NoPageContent
        iconName="MAGNIFYING-EXCLAMATION"
        title="No project found"
        message="This project could not be found"
      />
    );
  }
  return (
    <PosterProjectProvider
      initialProject={template?.project ?? initialProject}
      projectTemplate={template}
    >
      <Content
        contextType={contextType}
        isMobile={isTouchMode}
        onSave={() => {
          setContextType("rendering");
        }}
      />
    </PosterProjectProvider>
  );
};

const NoProjectFound = () => {
  return <div>No project found</div>;
};

const Loading = () => {
  return (
    <div className={styles.loadingContainer}>
      <ThreeDot color="lightGray" size="medium" text="" textColor="" />
    </div>
  );
};

interface ContentProps {
  contextType: EditorContextType;
  isMobile: boolean;
  onSave: () => void;
}
const Content = (props: ContentProps) => {
  return (
    <TooltipProvider>
      <EditorUIProvider>
        <ContentInner
          contextType={props.contextType}
          isMobile={props.isMobile}
          onSave={props.onSave}
        />
      </EditorUIProvider>
    </TooltipProvider>
  );
};

const ContentInner = (props: ContentProps) => {
  const { loadSignedURLS, saveDraft, project } = usePosterProject();
  const { checkout, home, navigateTo } = usePaths();
  const { isUserAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const { populateLabelsFromStrava, hasPopulatableLabels } =
    useStravaLabelPopulation();
  const { trackEvent } = useAnalyticsTracker();
  const { showToast } = useToast();
  const { setNavbarHidden } = useNavigationBarContext();
  const {
    editorUIState: { selectedViewportId, focusedViewportId },
    setSelectedViewportId,
  } = useEditorUI();
  const { formattedPriceForProject } = useCheckoutPricing();
  const { sizePresetFromProject } = useSizePresets();

  useEffect(() => {
    // Wait for auth to finish loading before loading signed URLs
    // This ensures userID is available for constructing Supabase storage paths
    if (!isAuthLoading) {
      loadSignedURLS();
    }
  }, [isAuthLoading, loadSignedURLS]);

  // Hide navbar on mobile editor
  useEffect(() => {
    if (props.isMobile) {
      setNavbarHidden(true);
    }
    return () => setNavbarHidden(false);
  }, [props.isMobile, setNavbarHidden]);

  // Prevent iOS Safari scroll issues in the editor (both mobile and desktop modes)
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
    document.body.style.top = "0";
    document.body.style.left = "0";

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.body.style.top = "";
      document.body.style.left = "";
    };
  }, []);

  // Close drawer when viewport is focused (tap-to-zoom) on mobile
  useEffect(() => {
    if (props.isMobile && focusedViewportId && isDrawerExpanded) {
      setIsDrawerExpanded(false);
    }
  }, [props.isMobile, focusedViewportId, isDrawerExpanded]);

  // Deselect viewport when clicking outside the poster
  useEffect(() => {
    if (!selectedViewportId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const posterElement = document.getElementById("posterContainerRef");
      if (posterElement && !posterElement.contains(event.target as Node)) {
        setSelectedViewportId(undefined);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedViewportId, setSelectedViewportId]);

  // Automatically populate labels when Strava data is imported
  useEffect(() => {
    if (project.stravaMetadata && hasPopulatableLabels()) {
      populateLabelsFromStrava(project.stravaMetadata);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.stravaMetadata?.importedAt]);

  const handleSave = () => {
    // Check for empty viewports in visible gallery before proceeding
    if (hasEmptyViewports(project.gallery)) {
      showToast(
        "Looks like you're missing some images in your project! Please fill each blank space before saving.",
        "warning",
        6000,
      );
      return;
    }

    // Always save locally first
    saveDraft();

    if (!isUserAuthenticated) {
      // Show auth modal for guests
      setShowAuthModal(true);
    } else {
      // Authenticated users proceed to rendering
      props.onSave();
    }
  };

  const containerClasses = props.isMobile
    ? `${styles.container} ${styles.containerMobile}`
    : styles.container;

  const sidebarClasses = props.isMobile
    ? `${styles.sidebarContainer} ${styles.sidebarContainerMobile}`
    : styles.sidebarContainer;

  const stageClasses = props.isMobile
    ? `${styles.editorStageContainer} ${styles.editorStageContainerMobile}`
    : styles.editorStageContainer;

  const handleHomeClick = () => {
    console.log("Navigating to home");
    navigateTo(home);
  };

  return (
    <>
      {/* Mobile editor logo - positioned outside container to avoid event capture issues */}
      {props.isMobile && (
        <button
          className={styles.editorLogo}
          onClick={() => handleHomeClick()}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleHomeClick();
          }}
          aria-label="Go to home"
        >
          <img src="/logo/LOGO.png" alt="TrailArt logo" />
        </button>
      )}
      <div className={containerClasses}>
        {/* Desktop sidebar - render first to appear on left */}
        {!props.isMobile && props.contextType !== "rendering" && (
          <div className={sidebarClasses}>
            <PosterEditorToolbar
              onClickSave={handleSave}
              isMobile={props.isMobile}
              isDrawerExpanded={isDrawerExpanded}
              onDrawerToggle={setIsDrawerExpanded}
            />
          </div>
        )}
        <div className={stageClasses}>
          <PosterStage
            onShowContextualEditor={(show) => {}}
            contextType={props.contextType}
            isMobile={props.isMobile}
            isDrawerExpanded={isDrawerExpanded}
            onDismissDrawer={() => setIsDrawerExpanded(false)}
          />
          <div className={styles.mapAttribution}>
            Map data from{" "}
            <a
              href="https://www.mapbox.com/about/maps"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mapbox
            </a>{" "}
            and{" "}
            <a
              href="http://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
            >
              OpenStreetMap
            </a>
          </div>
          {/* Mobile action bar - between poster and drawer, hidden when viewport is focused */}
          {props.isMobile &&
            props.contextType !== "rendering" &&
            !focusedViewportId && (
              <div className={styles.mobileActionBar}>
                <button
                  className={styles.mobileCheckoutButton}
                  onClick={() => {
                    trackEvent("Project Save Button clicked");
                    saveDraft();
                    handleSave();
                  }}
                >
                  <div className={styles.mobileCheckoutButtonInfo}>
                    <div className={styles.mobileCheckoutButtonDetails}>
                      <span className={styles.mobileCheckoutButtonDescription}>
                        {sizePresetFromProject(project)?.materialDescription ??
                          "Premium Poster"}{" "}
                        ·{" "}
                        {sizePresetFromProject(project)?.sizeDescription ?? ""}
                      </span>
                    </div>
                    <span className={styles.mobileCheckoutButtonPrice}>
                      {formattedPriceForProject(project)}
                    </span>
                  </div>
                  <div className={styles.mobileCheckoutButtonAction}>
                    <UIIcon iconName="BAG" variant="SELECTED" size={22} />
                    <UIIcon
                      iconName="CHEVRON-RIGHT"
                      variant="SELECTED"
                      size={18}
                    />
                  </div>
                </button>
              </div>
            )}
          {/* Mobile sidebar - render after stage to appear at bottom, hidden when viewport is focused */}
          {props.isMobile &&
            props.contextType !== "rendering" &&
            !focusedViewportId && (
              <div className={sidebarClasses}>
                <PosterEditorToolbar
                  onClickSave={handleSave}
                  isMobile={props.isMobile}
                  isDrawerExpanded={isDrawerExpanded}
                  onDrawerToggle={setIsDrawerExpanded}
                />
              </div>
            )}
        </div>
      </div>
      <AuthGateModal
        isShowing={showAuthModal}
        onDismiss={() => setShowAuthModal(false)}
        redirectPath={checkout}
      />
    </>
  );
};

const IncompatibleDeviceWarning = () => {
  // Extract template ID from URL if present
  const path = window.location.pathname;
  const parts = path.split("/");
  const type = parts[parts.length - 2];
  const id = parts[parts.length - 1];
  const templateId = type === "template" ? id : undefined;

  return <MobileEmailCapture templateId={templateId} />;
};
